import { jsx } from '@amazon/vinyl-tsx'
import type { ObservableValue } from '@amazon/vinyl-observable'
import type { Maybe } from '@amazon/vinyl-util'
import { reportClick } from '@/metrics/uiMetrics'
import { sanitizeRichText } from '@/util/richText'

export interface RichTextProps {
    readonly html: ObservableValue<Maybe<string>>
    readonly className?: string
    readonly visible?: ObservableValue<boolean>
    readonly classList?: ReadonlyArray<string | null | ObservableValue<string | null>>
    /** Podcast show and episode descriptions drop empty paragraphs, as GH's headers do. */
    readonly dropEmptyParagraphs?: true
    /** The uiClick a link press sends; GH's default is `selectAnchorLink`. */
    readonly linkActionType?: string
    readonly linkContentName?: string
}

/** GH's link handler: one uiClick with the pressed link as `targetUrl`; the anchor itself opens it. */
export function reportRichTextLinkPress(
    event: Event,
    linkActionType = 'selectAnchorLink',
    linkContentName?: string
): void {
    const href = (event.target as Element).closest('a')?.getAttribute('href')
    if (!href) return
    reportClick({
        actionType: linkActionType,
        targetUrl: href,
        ...(linkContentName ? { contentName: linkContentName } : null),
    })
}

/** GH's `RichText`: server HTML reduced to its allowlisted tags, each link press reported with its URL. */
export function RichText(props: RichTextProps) {
    const {
        html,
        dropEmptyParagraphs,
        linkActionType = 'selectAnchorLink',
        linkContentName,
    } = props
    return (
        <div
            {...(props.className ? { className: props.className } : null)}
            {...(props.classList ? { classList: props.classList } : null)}
            {...(props.visible ? { visible: props.visible } : null)}
            innerHTML={html.map((value) =>
                sanitizeRichText(value, dropEmptyParagraphs ? { dropEmptyParagraphs } : {})
            )}
            onclick={(event: Event) =>
                reportRichTextLinkPress(event, linkActionType, linkContentName)
            }
        />
    )
}
